// @ts-strict-ignore
import { LoggerService } from 'rainbow-web-sdk/services/core/logger.service';
import { AuthService } from 'rainbow-web-sdk/services/core/auth.service';
import { ErrorHelperInfo, ErrorHelperService } from 'rainbow-web-sdk/services/utilities/errorHelper.service';
import { ProfileService } from 'rainbow-web-sdk/services/core/profile.service';
import { ContactService } from 'rainbow-web-sdk/services/core/contact.service';
import { Contact } from 'rainbow-web-sdk/models/contact.model';
import { Service } from 'rainbow-web-sdk/services/service';
import { DirectoryInternalSearchResults, DirectorySearchOptions } from '../core/directorySearch.service';
import anonymizer from '../../utils/anonymizer';

export const OFFICE365_AD_SVC = 'Office365AdService';

export class Office365AdService extends Service {
    private logger: LoggerService = LoggerService.getInstance();
    private authService: AuthService = AuthService.getInstance();
    private errorHelperService: ErrorHelperService = ErrorHelperService.getInstance();
    private profileService: ProfileService = ProfileService.getInstance();
    private contactService: ContactService = ContactService.getInstance();

    public static getInstance() {
        return Service.create(OFFICE365_AD_SVC, Office365AdService);
    }

    public static build() {
        return new Office365AdService();
    }

    private constructor() { super(); }

    /**
     * Search contacts in the Microsoft O365/Azure AD directories configured for the user's company by using a specified pattern to filter items.
     * This search is only available if the user has the feature MSO365_DIRECTORY_SEARCH in its profile.
     * Concerning 'extendedMode' in 'options' parameter:
     * If requested and the user has the feature SEARCH_BY_TAGS in its profile, a multi-criteria search will then be performed.
     * The multi-criteria search applies on fields firstName, lastName, jobTitle, companyName, department and tags.
     * If this option is not specified or the user does not have the feature SEARCH_BY_TAGS in his profile,
     * the search is based on names only (first name, last name and company name).
     * Returns the results and the total number of directory items matching the criteria.
     * @param searchPattern - Pattern to search
     * @param DirectorySearchOptions - Search options
     */
    public async searchO365usersADcontacts(searchPattern: string, options?: DirectorySearchOptions): Promise<DirectoryInternalSearchResults> {
        // First, Check user licence
        if (!this.profileService.isFeatureEnabled(this.profileService.FeaturesEnum.MSO365_DIRECTORY_SEARCH)) {
            this.logger.debug(`[Office365AdService] searchO365usersADcontacts -- User does not have the profile feature MSO365_DIRECTORY_SEARCH`);
            return { contacts: [], total: 0 };
        }

        this.logger.info(`[Office365AdService]  search O365 contacts/AD users with pattern ${anonymizer(searchPattern)}`);

        try {
            // Define useful search options used in URL paramters / Body
            const searchOptions: any = {};
            if (options?.limit) { searchOptions.limit = options.limit; }

            // Simple OR Multi criterion search ?
            const searchByTagAllowed: boolean = this.profileService.isFeatureEnabled(this.profileService.FeaturesEnum.SEARCH_BY_TAGS);
            let searchType: string = "";

            if (options?.extendedMode) {
                if (searchByTagAllowed) {
                    searchType = options.extendedMode ? "search" : "name";
                } else {
                    if (options.extendedMode) {
                        this.logger.warn(`[Office365AdService] searchO365usersADcontacts -- 'extendedMode' option not allowed and ignored `);
                    }
                    searchType = "name";
                }
            } else {
                searchType = "name";
            }

            const body: any = {};
            body.includeContacts = true;
            if (searchType === "name") {
                body.name = searchPattern;

            } else {
                body.search = searchPattern;
            }

            // Build Request for Server
            const url: URL = new URL(`${config.restServerUrl}/api/rainbow/office365/v1.0/users/search`);
            const urlParams: string = new URLSearchParams(searchOptions).toString();
            const headers: any = this.authService.getPostHeader();
            url.search = urlParams;

            const response: any = await fetch(url.toString(), { method: 'POST', mode: 'cors', headers: headers, body: JSON.stringify(body) });

            if (!response.ok) { throw (response); }
            const responseData: any = (await response.json());
            const office365ContactsData: any = responseData.data;

            this.logger.info(`[Office365AdService] search success -- Found ${responseData.total} contact(s)`);

            const o365Contacts: Contact[] = office365ContactsData.map((office365ContactData: any) => {
                const contact: Contact = this.contactService.createDirectoryContact(office365ContactData.oid, false);
                this.updateContactWithData(contact, office365ContactData);
                return contact;
            });

            this.logger.info(`[Office365AdService] searchO365usersADcontacts -- Return ${o365Contacts.length} contact(s)`);

            return { contacts: o365Contacts, total: responseData.total };
        } catch (error) {
            const errorInfo: ErrorHelperInfo = await this.errorHelperService.getErrorInfo(error, 'searchO365usersADcontacts');
            this.logger.error(`[Office365AdService] ${errorInfo.message}`);
            if (errorInfo.error.status === 403) {
                // Request rejected by server probably because of a user license problem
                // Return a "no results found" like reponse
                return { contacts: [], total: 0 };
            }
            throw errorInfo.error;
        }
    }

    public async getUserDetails(contact: any) {
        // Verification of the O365 Search Profile and that contact is indeed an Azure contact.
        if (!this.profileService.isFeatureEnabled(this.profileService.FeaturesEnum.MSO365_DIRECTORY_SEARCH) || !contact.o365Flag) return;

        this.logger.info(`[Office365AdService] getUserDetails for ${contact.id}`);
        try {
            const headers = this.authService.getRequestHeader();
            const url = `${config.restServerUrl}/api/rainbow/office365/v1.0/users/details/${encodeURIComponent(contact.id)}?picture=true`;
            const responseData: any = await fetch(url, { method: "GET", mode: "cors", headers });
            if (!responseData.ok) {
                this.logger.debug(`[Office365AdService] Request ${responseData.headers.get(`x-rainbow-request-id`)} failed with status ${responseData.status}`);
                throw new Error(responseData.statusText);
            }
            const response: any = await responseData.json();
            const office365ContactData: any = response;
            this.updateContactWithOffice365Data(contact, office365ContactData);
        } catch (error) {
            this.logger.error(`[Office365AdService] getUserDetails -- FAILURE -- ${error.message}`);
        }
    }

    /**
     * This API allows users to retrieve from server one Microsoft Office 365 Azure contact card from its identifier
     * (API request to {@link https://api.openrainbow.org/office365/#api-users-getOffice365UserDetailsByOid Office365 portal - users - Get user details.
     * A new contact based on Contact model is created for the found contact.
     * @param oid - Office 365 Azure directory object identifier of the contact to retrieve
     */
    public async getO365ContactFromServer(oid: any): Promise<Contact | null> {
        this.logger.info(`[Office365Ad  Service] getO365ContactFromServer for ${oid}`);

        if (!this.profileService.isFeatureEnabled(this.profileService.FeaturesEnum.MSO365_DIRECTORY_SEARCH)) {
            this.logger.info(`[Office365AdService] getO365ContactFromServer -- User does not have the profile feature MSO365_DIRECTORY_SEARCH`);
            return null;
        }

        try {
            const headers = this.authService.getRequestHeader();
            const url = `${config.restServerUrl}/api/rainbow/office365/v1.0/users/details/${encodeURIComponent(oid)}`;
            const responseData: any = await fetch(url, { method: "GET", mode: "cors", headers });
            if (!responseData.ok) {
                this.logger.error(`[Office365AdService] Request ${responseData.headers.get(`x-rainbow-request-id`)} failed -- status ${responseData.status} - statusText ${responseData.statusText}`);
                throw new Error(responseData.statusText);
            }
            const response: any = await responseData.json();
            const office365ContactData: any = response;

            // Create a new Contact associated to the found o365 contact
            const o365Contacts: Contact = this.contactService.createDirectoryContact(oid, false);

            if (!o365Contacts) {
                this.logger.error(`[Office365AdService] getO365ContactFromServer -- Contact creation failed for ${oid}`);
                return null;
            }

            this.updateContactWithOffice365Details(o365Contacts, office365ContactData);

            return o365Contacts;
        } catch (error) {
            this.logger.error(`[Office365AdService] getO365ContactFromServer -- FAILURE -- ${error.message}`);
            return null;
        }
    }

    public async getO365DirectoryContactById(oid: string): Promise<Contact> {
        // The O365 Directory contact is always be retrieved from the server
        const directoryContact: Contact = await this.getO365ContactFromServer(oid);

        if (directoryContact) {
            this.logger.info(`[Office365AdService] getO365DirectoryContactById Success -- Contact ${oid} retrieved from server and created in cache`);
        }
        else {
            this.logger.error(`[Office365AdService] getO365DirectoryContactById Failure -- Contact ${oid} unknown from server or problem when retrieving it`);
        }

        return directoryContact;
    }

    private updateContactWithData(contact: any, data: any) {
        contact.o365Flag = true;
        contact.updateName(data.firstName, data.lastName);
        contact.companyName = data.companyName ? data.companyName : "";
        contact.jobTitle = data.jobTitle;
        contact.department = data.department;
        contact.webAddress = data.mySite;
        contact.mobilePhones = data.mobilePhonesE164;
        contact.businessPhones = data.businessPhonesE164;
        contact.email = data.mail;
        contact.isContact = data.isContact;
    }

    private updateContactWithOffice365Data(contact: any, data: any) {
        contact.o365Flag = true;
        contact.updateName(data.givenName, data.surname);
        contact.companyName = data.companyName ? data.companyName : "";
        contact.jobTitle = data.jobTitle;
        contact.department = data.department;
        contact.webAddress = data.mySite;
        contact.email = data.mail;

        if (data.isContact) {
            contact.street = data.addresses[0]?.street ? data.addresses[0].street : "";
            contact.city = data.addresses[0]?.city ? data.addresses[0].city : "";
            contact.country = data.addresses[0]?.countryOrRegion ? data.addresses[0].countryOrRegion : "";
            contact.postalCode = data.addresses[0]?.postalCode ? data.addresses[0].postalCode : "";
            contact.state = data.addresses[0]?.state ? data.addresses[0].state : "";
        } else {
            contact.street = data.streetAddress;
            contact.city = data.city;
            contact.country = data.country;
            contact.postalCode = data.postalCode;
            contact.state = data.state;
        }

        if (data.isContact && data.phones && data.phones.length > 0) {
            contact.businessPhones = [];
            contact.mobilePhones = [];
            data.phones.forEach((phoneNumber: any) => {
                if (phoneNumber.number && phoneNumber.number.length > 0) {
                    if (phoneNumber.type === "mobile") {
                        contact.mobilePhones.push(phoneNumber.number);
                    } else if (phoneNumber.type === "business") {
                        contact.businessPhones.push(phoneNumber.number);
                    }
                }
            });
        }
        else {
            contact.businessPhones = data.businessPhones;
            contact.mobilePhones = [];
            if (data.mobilePhone && data.mobilePhone !== "") {
                // If present in the phone number, spaces are not removed
                // The phone number wil be displayed as set on Microsoft Azure and sent so by server
                contact.mobilePhones.push(data.mobilePhone);
            }
        }

        if (data.picture) {
            let dataURI: string = null;

            // Check the first 3 char of base64 picture value to find the image signature
            if (data.picture.startsWith("/9j")) {
                dataURI = "data:image/jpeg;base64,";
            } else if (data.picture.startsWith("iVB")) {
                dataURI = "data:image/png;base64,";
            }

            // Picture taken in account only if a jpeg/png signature has been found
            // Otherwise, the image is not stored and the default avatar (based on initials) will be used
            if (dataURI) {
                contact.avatar = new Image();
                contact.avatar.src = dataURI + data.picture;
            }
        }
    }

    /**
     * Method updateContactWithOffice365Details
     * @description Update an existing o365 contact with o365 complete data retrieved from the server
     * @memberof Office365AdService
     * @public
     * @param {Contact} contact The contact The o365 contact to update
     * @param {any} data  The o365 complete data retrieved from the server
     */
    private updateContactWithOffice365Details(contact: Contact, data: any) {
        contact.o365Flag = true;
        contact.updateName(data.givenName, data.surname);
        contact.companyName = data.companyName ? data.companyName : "";
        contact.jobTitle = data.jobTitle;
        contact.department = data.department;
        // contact.webAddress = data.mySite;
        contact.email = data.mail;

        if (data.isContact) {
            contact.street = data.addresses[0]?.street ? data.addresses[0].street : "";
            contact.city = data.addresses[0]?.city ? data.addresses[0].city : "";
            contact.country = data.addresses[0]?.countryOrRegion ? data.addresses[0].countryOrRegion : "";
            contact.postalCode = data.addresses[0]?.postalCode ? data.addresses[0].postalCode : "";
            contact.state = data.addresses[0]?.state ? data.addresses[0].state : "";
        } else {
            contact.street = data.streetAddress;
            contact.city = data.city;
            contact.country = data.country;
            contact.postalCode = data.postalCode;
            contact.state = data.state;
        }

        if (data.isContact && data.phones && data.phones.length > 0) {
            contact.businessPhones = [];
            contact.mobilePhones = [];
            data.phones.forEach((phoneNumber: any) => {
                if (phoneNumber.number && phoneNumber.number.length > 0) {
                    if (phoneNumber.type === "mobile") {
                        contact.mobilePhones.push(phoneNumber.number);
                    } else if (phoneNumber.type === "business") {
                        contact.businessPhones.push(phoneNumber.number);
                    }
                }
            });
        }
        else {
            contact.businessPhones = data.businessPhones;
            contact.mobilePhones = [];
            if (data.mobilePhone && data.mobilePhone !== "") {
                // If present in the phone number, spaces are not removed
                // The phone number wil be displayed as set on Microsoft Azure and sent so by server
                contact.mobilePhones.push(data.mobilePhone);
            }
        }

        if (data.picture) {
            let dataURI: string = null;

            // Check the first 3 char of base64 picture value to find the image signature
            if (data.picture.startsWith("/9j")) {
                dataURI = "data:image/jpeg;base64,";
            } else if (data.picture.startsWith("iVB")) {
                dataURI = "data:image/png;base64,";
            }

            // Picture taken in account only if a jpeg/png signature has been found
            // Otherwise, the image is not stored and the default avatar (based on initials) will be used
            if (dataURI) {
                contact.avatar = new Image();
                contact.avatar.src = dataURI + data.picture;
            }
        }
    }

    // -------------------------------------------------
    //      OUTLOOK Personal COntact Management
    // -------------------------------------------------

    /**
     * Update the specified exisitng RB contact instance with some provided Microsoft Outlook contact data.
     */
    private updateContactWithOutlookSearchData(contact: Contact, outlookData: any) {
        contact.outlookFlag = true;

        contact.title = outlookData.title ? outlookData.title : "";
        contact.updateName(outlookData.givenName, outlookData.surname);
        contact.nickname = outlookData.nickname ? outlookData.nickname : "";

        // This information from Outlook will override the information
        // automatically generated by Rainbow from the first and last names.
        if (outlookData.initials) {
            contact.initials = outlookData.initials;
        }

        contact.street = outlookData.businessAddress.street;
        contact.postalCode = outlookData.businessAddress.postalCode;
        contact.city = outlookData.businessAddress.city;
        contact.state = outlookData.businessAddress.state;
        contact.country = outlookData.businessAddress.countryOrRegion;

        contact.jobTitle = outlookData.jobTitle;
        contact.companyName = outlookData.companyName ? outlookData.companyName : "";
        contact.department = outlookData.department ? outlookData.department : "";

        // No 'webAddress' property in contact model. Kept in case if one day...
        // contact.webAddress = outlookData.webAddress ? outlookData.webAddress : "";

        // Only ONE business (Work) phone number is taken into account as for Rainbow users, personal and business contacts.
        // If we retrieved them all, this would cause a display problem on certain screens (telephony, bubble)
        // where the mobile number would no longer be displayed.
        contact.businessPhones = [];
        if (outlookData.businessPhonesE164 && outlookData.businessPhonesE164.length > 0) {
            contact.businessPhones.push(outlookData.businessPhonesE164[0]);
        }

        contact.mobilePhones = [];
        // If present in the phone number, spaces are not removed
        // The phone number wil be displayed as set on Outlook and sent so by server
        if (outlookData.mobilePhoneE164 && outlookData.mobilePhoneE164 !== "") {
            contact.mobilePhones.push(outlookData.mobilePhoneE164);
        }

        if (outlookData.emailAddresses && outlookData.emailAddresses.length > 0) {
            contact.email = outlookData.emailAddresses[0].address;
        }

        if (outlookData.picture) {
            let dataURI: string = null;

            // Check the first 3 char of base64 picture value to find the image signature
            if (outlookData.picture.startsWith("/9j")) {
                dataURI = "data:image/jpeg;base64,";
            } else if (outlookData.picture.startsWith("iVB")) {
                dataURI = "data:image/png;base64,";
            }

            // Picture taken in account only if a jpeg/png signature has been found
            // Otherwise, the image is not stored and the default avatar (based on initials) will be used
            if (dataURI) {
                contact.avatar = new Image();
                contact.avatar.src = dataURI + outlookData.picture;
            }
        }

        // IMPORTANT WARNING:
        // The displayname field is automatically deduced by the Web Client based on the contact's first and last names via updateName().
        // However, if it is provided in an outlook contacvt, we will give preference to the latter.
        // BUT the 'displayName' field must be updated in the contract AFTER the avatar image to avoid avatar display issues
        // in favorites section ('displayName' setter send an rxSubject contact "name" event that may break avatar priority).
        if (outlookData.displayName) {
            contact.displayName = outlookData.displayName;
        }
    }

    /**
     * This API allows users to retrieve from server one Microsoft Outlook contact card from its identifier
     * and update the given contact. No new contact instance is created.
     * @param contact - The internal contact to update with additional or last Outlook contact data.
     */
    public async getOutlookContactDetails(contact: Contact): Promise<Contact | null> {
        this.logger.info(`[Office365AdService] getOutlookContactDetails for ${contact.id}`);

        // Check the O365 Search Profile
        if (!this.profileService.isFeatureEnabled(this.profileService.FeaturesEnum.MSO365_DIRECTORY_SEARCH)) {
            this.logger.warn(`[Office365AdService] getOutlookContactDetails -- User does not have the profile feature MSO365_DIRECTORY_SEARCH`);
            return null;
        }

        // Check that contact is indeed an Outlook contact.
        if (!contact.outlookFlag) {
            this.logger.warn(`[Office365AdService] getOutlookContactDetails -- contact (id: ${contact.id}) is not an Outlook contact`);
            return;
        }

        try {
            const headers = this.authService.getRequestHeader();
            const url = `${config.restServerUrl}/api/rainbow/office365/v1.0/contacts/details/${encodeURIComponent(contact.id)}?picture=true`;
            const responseData: any = await fetch(url, { method: "GET", mode: "cors", headers });
            if (!responseData.ok) {
                this.logger.error(`[Office365AdService] Request ${responseData.headers.get(`x-rainbow-request-id`)} failed -- status ${responseData.status} - statusText ${responseData.statusText}`);
                throw new Error(responseData.statusText);
            }
            const response: any = await responseData.json();
            const outlookContactData: any = response;

            this.updateContactWithOutlookSearchData(contact, outlookContactData);

            // return o365Contact;
        } catch (error) {
            this.logger.error(`[Office365AdService] getOutlookContactDetails -- FAILURE -- ${error.message}`);
            return null;
        }
    }

    /**
     * This API allows users to retrieve from server one Microsoft Outlook contact card from its identifier.
     * A new contact based on Contact model is created for the found contact.
     * @param oid - Identifier of the Outlook contact to retrieve
     */
    public async getOutlookContactById(oid: string): Promise<Contact> {
        this.logger.info(`[Office365AdService] getOutlookContactById for ${oid}`);

        if (!this.profileService.isFeatureEnabled(this.profileService.FeaturesEnum.MSO365_DIRECTORY_SEARCH)) {
            this.logger.info(`[Office365AdService] getOutlookContactById -- User does not have the profile feature MSO365_DIRECTORY_SEARCH`);
            return null;
        }

        try {
            const headers = this.authService.getRequestHeader();
            const url = `${config.restServerUrl}/api/rainbow/office365/v1.0/contacts/details/${encodeURIComponent(oid)}?picture=true`;
            const responseData: any = await fetch(url, { method: "GET", mode: "cors", headers });
            if (!responseData.ok) {
                this.logger.error(`[Office365AdService] Request ${responseData.headers.get(`x-rainbow-request-id`)} failed -- status ${responseData.status} - statusText ${responseData.statusText}`);
                throw new Error(responseData.statusText);
            }
            const response: any = await responseData.json();
            const outlookContactData: any = response;

            // Create a new Contact associated to the found o365 contact
            const outlookContact: Contact = this.contactService.createDirectoryContact(oid, false);

            if (!outlookContact) {
                this.logger.error(`[Office365AdService] getOutlookContactById -- Contact creation failed for ${oid}`);
                return null;
            }

            this.updateContactWithOutlookSearchData(outlookContact, outlookContactData);

            return outlookContact;
        } catch (error) {
            this.logger.error(`[Office365AdService] getOutlookContactById -- FAILURE -- ${error.message}`);
            return null;
        }
    }

    /**
     * Search personal contacts in the Microsoft Outlook directory by specifying a string pattern to filter items.
     * The pattern is the beginning of a name or a phone number.
     * This search is only available if the user has the feature MSO365_DIRECTORY_SEARCH in its profile.
     * Returns the results items matching the criteria.
     * @param searchPattern - Pattern to search
     */
    public async searchOutlookPersonalcontacts(searchPattern: string): Promise<DirectoryInternalSearchResults> {
        // First, Check user licence
        if (!this.profileService.isFeatureEnabled(this.profileService.FeaturesEnum.MSO365_DIRECTORY_SEARCH)) {
            this.logger.debug(`[Office365AdService] searchOutlookPersonalcontacts -- User does not have the profile feature MSO365_DIRECTORY_SEARCH`);
            return { contacts: [], total: 0 };
        }

        this.logger.info(`[Office365AdService] searchOutlookPersonalcontacts - search Outlook personal contacts with pattern ${anonymizer(searchPattern)}`);

        try {
            // Build Request for Server
            const url = `${config.restServerUrl}/api/rainbow/office365/v1.0/contacts/search?search=${searchPattern}`;
            const response: any = await fetch(url, { method: 'GET', mode: 'cors', headers: this.authService.getRequestHeader() });

            if (!response.ok) { throw (response); }
            const responseData: any = (await response.json());

            this.logger.info(`[Office365AdService] searchOutlookPersonalcontacts success -- Found ${responseData.total} contact(s)`);

            const outlookContacts: Contact[] = responseData.data.map((outlookContactData: any) => {
                const contact: Contact = this.contactService.createDirectoryContact(outlookContactData.oid, false);
                this.updateContactWithOutlookSearchData(contact, outlookContactData);
                return contact;
            });

            this.logger.info(`[Office365AdService] searchOutlookPersonalcontacts -- Return ${outlookContacts.length} contact(s)`);

            return { contacts: outlookContacts, total: outlookContacts.length };
        } catch (error) {
            const errorInfo: ErrorHelperInfo = await this.errorHelperService.getErrorInfo(error, 'searchOutlookPersonalcontacts');
            this.logger.error(`[Office365AdService] ${errorInfo.message}`);
            if (errorInfo.error.status === 403) {
                // Request rejected by server probably because of a user license problem
                // Return a "no results found" like reponse
                return { contacts: [], total: 0 };
            }
            throw errorInfo.error;
        }
    }

}
